import { loadSilks, drawSilk, fit } from "@/components/ShareButtons";
import { raceMs, clock12 } from "@/lib/raceFormat";

const W = 1080, H = 1350, PER_PAGE = 4, ROW_H = 186, ROW_GAP = 12;
const NAVY = "#011B45", BODY = "#06163A", BLUE = "#1E90FF", ROW = "rgba(16,38,86,0.55)", EDGE = "#1E3A6E";
const ROLE = { "top pick": ["TOP PICK", "#1E90FF"], danger: ["DANGER", "#475569"], roughie: ["ROUGHIE", "#059669"] };
const VERDICT = { value: ["Value", "#34D399"], fair: ["Fair price", "#CBD5E1"], under: ["Under", "#FB7185"] };
const SANS = "Outfit, 'DM Sans', sans-serif";

const loadImg = (src) => new Promise((ok, bad) => { const i = new Image(); i.onload = () => ok(i); i.onerror = bad; i.src = src; });
const spaced = (ctx, px) => { if ("letterSpacing" in ctx) ctx.letterSpacing = `${px}px`; };

const shrink = (ctx, text, maxW, size, min, weight = 700) => {
  for (let s = size; s >= min; s -= 1) {
    ctx.font = `${weight} ${s}px ${SANS}`;
    if (ctx.measureText(text).width <= maxW) return text;
  }
  return fit(ctx, text, maxW);
};

const wordmark = (ctx, x, y, s) => {
  ctx.textAlign = "left";
  ctx.fillStyle = "#fff"; ctx.font = `800 ${Math.round(s * 0.42)}px ${SANS}`; spaced(ctx, s * 0.12); ctx.fillText("THE", x + s * 0.05, y - s * 0.95); spaced(ctx, 0);
  ctx.font = `italic 900 ${s}px ${SANS}`; ctx.fillText("WEEKEND", x, y);
  ctx.fillStyle = BLUE; ctx.fillText("EDGE", x + ctx.measureText("WEEKEND ").width, y);
  ctx.fillStyle = "#93A4C3"; ctx.font = `600 ${Math.round(s * 0.3)}px ${SANS}`; spaced(ctx, s * 0.09);
  const tag = "AUSTRALIAN RACING TIPS", tw = ctx.measureText(tag).width, ty = y + s * 0.62;
  ctx.fillText(tag, x + s * 0.9, ty); spaced(ctx, 0);
  ctx.fillStyle = BLUE; ctx.fillRect(x, ty - s * 0.12, s * 0.72, 3); ctx.fillRect(x + s * 1.08 + tw, ty - s * 0.12, s * 0.72, 3);
};

const header = (ctx, logo, hero) => {
  ctx.fillStyle = NAVY; ctx.fillRect(0, 0, W, 250);
  if (hero) {
    ctx.save(); ctx.beginPath(); ctx.moveTo(810, 0); ctx.lineTo(W, 0); ctx.lineTo(W, 250); ctx.lineTo(745, 250); ctx.closePath(); ctx.clip();
    const s = Math.max(335 / hero.width, 250 / hero.height);
    ctx.drawImage(hero, 745 + (335 - hero.width * s) / 2, (250 - hero.height * s) / 2, hero.width * s, hero.height * s);
    const g = ctx.createLinearGradient(745, 0, 920, 0); g.addColorStop(0, NAVY); g.addColorStop(1, "rgba(1,27,69,0.05)");
    ctx.fillStyle = g; ctx.fillRect(745, 0, 175, 250); ctx.restore();
    ctx.strokeStyle = BLUE; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(810, 0); ctx.lineTo(745, 250); ctx.stroke();
  }
  if (logo) ctx.drawImage(logo, 24, 44, 230, 230 * (logo.height / logo.width));
  wordmark(ctx, 246, 150, 56);
  ctx.fillStyle = BLUE; ctx.fillRect(0, 250, W, 4);
};

const meetingBand = (ctx, s, page, pages) => {
  ctx.fillStyle = "#071A40"; ctx.fillRect(0, 254, W, 140);
  const d = new Date(`${s.date}T00:00:00`);
  ctx.textAlign = "left"; ctx.fillStyle = "#fff";
  ctx.fillText(shrink(ctx, s.track.toUpperCase(), 560, 64, 40, 900), 52, 326);
  ctx.fillStyle = BLUE; ctx.font = `800 32px ${SANS}`;
  ctx.fillText(d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).toUpperCase().replace(/^(\w+)/, "$1,"), 52, 368);
  ctx.fillStyle = EDGE; ctx.fillRect(640, 278, 3, 96);
  ctx.fillStyle = "#CBD5E1"; ctx.font = `600 26px ${SANS}`; spaced(ctx, 1.5);
  ctx.fillText(`${s.track.toUpperCase()}${s.state ? `, ${s.state}` : ""}`.slice(0, 26), 672, 318);
  if (s.track_condition) ctx.fillText(`TRACK: ${s.track_condition.toUpperCase()}`, 672, 360);
  spaced(ctx, 0);
  if (pages > 1) { ctx.textAlign = "right"; ctx.fillStyle = "#64748B"; ctx.font = `600 20px ${SANS}`; ctx.fillText(`${page + 1}/${pages}`, W - 28, 380); ctx.textAlign = "left"; }
  ctx.fillStyle = BLUE; ctx.fillRect(0, 394, W, 2);
};

const nameLines = (ctx, text, maxW) => {
  for (let sz = 23; sz >= 19; sz -= 1) {
    ctx.font = `700 ${sz}px ${SANS}`;
    if (ctx.measureText(text).width <= maxW) return [[text], sz];
  }
  const words = text.split(" ");
  for (let k = words.length - 1; k > 1; k -= 1) {
    const a = words.slice(0, k).join(" ");
    if (ctx.measureText(a).width <= maxW) return [[a, fit(ctx, words.slice(k).join(" "), maxW)], 19];
  }
  for (let sz = 18; sz >= 15; sz -= 1) {
    ctx.font = `700 ${sz}px ${SANS}`;
    if (ctx.measureText(text).width <= maxW) return [[text], sz];
  }
  return [[fit(ctx, text, maxW)], 15];
};

const pickCell = (ctx, p, silk, x, y, w) => {
  const [label, col] = ROLE[p.role];
  ctx.font = `800 19px ${SANS}`; spaced(ctx, 1); const lw = ctx.measureText(label).width + 28;
  ctx.fillStyle = col; ctx.beginPath(); ctx.roundRect(x + 12, y + 14, lw, 32, 4); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.fillText(label, x + 26, y + 37); spaced(ctx, 0);
  if (silk) { const k = Math.min(84 / silk.width, 84 / silk.height); ctx.drawImage(silk, x + 8 + (84 - silk.width * k) / 2, y + 62 + (84 - silk.height * k) / 2, silk.width * k, silk.height * k); } else drawSilk(ctx, null, x + 8, y + 62, 84);
  const tx = x + 100, tw = w - 108;
  const [lines, sz] = nameLines(ctx, `${p.number}. ${p.horse.toUpperCase()}`, tw);
  ctx.fillStyle = "#fff"; ctx.font = `700 ${sz}px ${SANS}`;
  let ty = y + (lines.length > 1 ? 76 : 86);
  lines.forEach((l) => { ctx.fillText(l, tx, ty); ty += sz + 3; });
  const nums = [p.est_win_prob != null && `${Math.round(p.est_win_prob * 100)}%`, p.odds && `$${Number(p.odds).toFixed(2)}`].filter(Boolean).join("  |  ");
  ctx.fillStyle = "#CBD5E1"; ctx.font = `600 24px ${SANS}`; if (nums) ctx.fillText(nums, tx, ty + 22);
  const v = VERDICT[p.verdict];
  if (v) { ctx.fillStyle = v[1]; ctx.font = `700 19px ${SANS}`; ctx.fillText(fit(ctx, p.edge != null && p.verdict !== "fair" ? `${v[0]} ${p.edge > 0 ? "+" : ""}${(p.edge * 100).toFixed(1)} pts` : v[0], tw), tx, ty + 52); }
};

const raceRow = (ctx, r, silks, date, y, h) => {
  ctx.fillStyle = ROW; ctx.strokeStyle = EDGE; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.roundRect(24, y, W - 48, h, 6); ctx.fill(); ctx.stroke();
  ctx.textAlign = "left"; ctx.fillStyle = "#fff"; ctx.font = `900 46px ${SANS}`; ctx.fillText(`RACE ${r.number}`, 44, y + 66);
  ctx.font = `700 30px ${SANS}`; ctx.fillText(clock12(raceMs(r, date)).toUpperCase(), 44, y + 110);
  ctx.fillStyle = "#93A4C3"; ctx.font = `600 24px ${SANS}`;
  ctx.fillText(fit(ctx, [r.distance && `${r.distance}m`, r.race_class && r.race_class.replace(/bench\s*mark\s*/i, "BM").replace(/^class\s*/i, "CL")].filter(Boolean).join("  |  "), 200), 44, y + 150);
  ctx.fillStyle = EDGE; ctx.fillRect(254, y + 14, 2, h - 28);
  const cw = (W - 48 - 236) / 3;
  ["top pick", "danger", "roughie"].forEach((role, i) => {
    const x = 260 + i * cw, p = r.picks.find((q) => q.role === role);
    if (i) { ctx.fillStyle = "rgba(30,58,110,0.6)"; ctx.fillRect(x, y + 14, 1, h - 28); }
    if (p) return pickCell(ctx, p, silks[p.silk_url], x, y, cw);
    ctx.fillStyle = "#64748B"; ctx.font = `600 20px ${SANS}`;
    ctx.fillText(role === "roughie" ? "No value pick" : "Selection pending", x + 16, y + h / 2 + 8);
  });
};

const footer = (ctx, logo) => {
  const y = 1214;
  if (logo) ctx.drawImage(logo, 36, y, 120, 120 * (logo.height / logo.width));
  wordmark(ctx, 160, y + 70, 34);
  ctx.fillStyle = EDGE; ctx.fillRect(560, y + 8, 2, 82);
  ctx.fillStyle = "#CBD5E1"; ctx.font = `600 21px ${SANS}`; spaced(ctx, 3); ctx.textAlign = "center";
  ctx.fillText("BETTER ANALYSIS. SMARTER TIPS.", 815, y + 56); spaced(ctx, 0);
  ctx.fillStyle = "#64748B"; ctx.font = `500 19px ${SANS}`;
  ctx.fillText("Opinion only, not a guarantee. 18+ Gamble responsibly. 1800 858 858", W / 2, H - 16); ctx.textAlign = "left";
};

const draftMark = (ctx) => {
  ctx.save(); ctx.translate(W / 2, 800); ctx.rotate(-0.5); ctx.fillStyle = "rgba(251,191,36,0.14)";
  ctx.font = `900 220px ${SANS}`; ctx.textAlign = "center"; ctx.fillText("DRAFT", 0, 80); ctx.restore();
};

export async function drawSheetPages(sheet) {
  await document.fonts.ready;
  const [logo, hero] = await Promise.all([loadImg("/logo-horse.png").catch(() => null), loadImg("/sheet-hero.jpg").catch(() => null)]);
  const urls = [...new Set(sheet.races.flatMap((r) => r.picks.map((p) => p.silk_url)).filter(Boolean))];
  const imgs = await loadSilks(urls);
  const silks = Object.fromEntries(urls.map((u, i) => [u, imgs[i]]));
  const pages = Math.max(1, Math.ceil(sheet.races.length / PER_PAGE));
  const blobs = [];
  for (let p = 0; p < pages; p += 1) {
    const c = document.createElement("canvas"); c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = BODY; ctx.fillRect(0, 0, W, H);
    header(ctx, logo, hero);
    meetingBand(ctx, sheet, p, pages);
    sheet.races.slice(p * PER_PAGE, (p + 1) * PER_PAGE).forEach((r, i) => raceRow(ctx, r, silks, sheet.date, 410 + i * (ROW_H + ROW_GAP), ROW_H));
    footer(ctx, logo);
    if (sheet.status !== "approved") draftMark(ctx);
    blobs.push(await new Promise((ok) => c.toBlob(ok, "image/png")));
  }
  return blobs;
}
